import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from '@zt/i18n';
import { Badge, Button, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { useMe, useSession } from '../lib/session';
import { MobileNotificationsSection } from './push';

interface SessionRow {
  id: string;
  kind: 'browser' | 'mobile';
  ip: string | null;
  userAgent: string | null;
  lastSeenAt: string;
  current: boolean;
}

export function AccountPage() {
  const t = useT();
  const { formatRelative, setLocale } = useI18n();
  const me = useMe();
  const session = useSession();
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: ['my-sessions'],
    queryFn: () => api.get<SessionRow[]>('/v1/admin/me/sessions'),
  });
  const [profile, setProfile] = useState<{ name: string; phone: string } | null>(null);
  const [pw, setPw] = useState({ current: '', next: '' });

  const update = useApiMutation((body: Record<string, unknown>) => api.patch('/v1/admin/me', body), {
    invalidate: [['me'], ['session'], ['help']],
    success: t('common.saved'),
    onSuccess: (_r, v) => {
      if (typeof v.locale === 'string') setLocale(v.locale as Locale);
      setProfile(null);
    },
  });
  const changePw = useApiMutation(
    () => api.post('/v1/auth/password/change', { currentPassword: pw.current, newPassword: pw.next }),
    {
      success: t('admin.account.passwordChanged'),
      onSuccess: () => setPw({ current: '', next: '' }),
      silentFieldErrors: true,
    },
  );
  const revoke = useApiMutation((id: string) => api.del(`/v1/admin/me/sessions/${id}`), {
    invalidate: [['my-sessions']],
  });
  const switchOrg = useApiMutation(
    (organizationId: string) => api.post('/v1/auth/switch-organization', { organizationId }),
    {
      onSuccess: () => qc.invalidateQueries(),
    },
  );

  if (!me.data)
    return (
      <PageFrame helpKey="page.account">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const u = me.data.user;
  const p = profile ?? { name: u.name, phone: u.phone ?? '' };

  return (
    <PageFrame helpKey="page.account">
      <Section title={t('admin.account.profile')}>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label={t('common.name')} required>
            {(fp) => (
              <Input {...fp} value={p.name} onChange={(e) => setProfile({ ...p, name: e.target.value })} />
            )}
          </Field>
          <Field label={t('admin.account.phone')} optionalLabel={t('common.optional')}>
            {(fp) => (
              <Input
                {...fp}
                type="tel"
                value={p.phone}
                onChange={(e) => setProfile({ ...p, phone: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('common.email')}>{(fp) => <Input {...fp} value={u.email} disabled />}</Field>
        </div>
        {profile && (
          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={() => setProfile(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={update.isPending}
              onClick={() => update.mutate({ name: p.name, phone: p.phone || null })}
            >
              {t('common.save')}
            </Button>
          </div>
        )}
      </Section>

      <Section title={t('admin.account.uiMode')}>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label={t('admin.account.language')} help={<HelpTip k="account.language" />}>
            {(fp) => (
              <Select {...fp} value={u.locale} onChange={(e) => update.mutate({ locale: e.target.value })}>
                {SUPPORTED_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {LOCALE_NAMES[l]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.account.uiMode')} help={<HelpTip k="account.ui_mode" />}>
            {(fp) => (
              <Select {...fp} value={u.uiMode} onChange={(e) => update.mutate({ uiMode: e.target.value })}>
                <option value="beginner">{t('admin.account.beginner')}</option>
                <option value="advanced">{t('admin.account.advanced')}</option>
              </Select>
            )}
          </Field>
        </div>
      </Section>

      <Section title={t('admin.account.password')}>
        <form
          className="grid gap-5 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            changePw.mutate(undefined);
          }}
        >
          <Field
            label={t('admin.account.currentPassword')}
            required
            error={changePw.error?.code === 'invalid_credentials' ? changePw.error.title : undefined}
          >
            {(fp) => (
              <Input
                {...fp}
                type="password"
                autoComplete="current-password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.account.newPassword')}
            required
            description={t('admin.auth.passwordHint')}
            error={fieldError(changePw.error, 'password')}
          >
            {(fp) => (
              <Input
                {...fp}
                type="password"
                autoComplete="new-password"
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
              />
            )}
          </Field>
          <div className="flex items-center gap-1 sm:col-span-2">
            <Button
              type="submit"
              variant="primary"
              loading={changePw.isPending}
              disabled={!pw.current || !pw.next}
            >
              {t('admin.account.changePassword')}
            </Button>
            <HelpTip k="account.change_password" />
          </div>
        </form>
      </Section>

      <Section title={t('admin.account.sessions')}>
        {!sessions.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {sessions.data.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {s.kind === 'mobile' ? t('admin.account.mobile') : t('admin.account.browser')}
                  </p>
                  <p className="truncate text-sm text-ink-3">{s.userAgent ?? '—'}</p>
                </div>
                <span className="text-sm text-ink-3">{s.ip}</span>
                <span className="text-sm text-ink-3">{formatRelative(s.lastSeenAt)}</span>
                {s.current ? (
                  <Badge tone="pine">{t('admin.account.thisDevice')}</Badge>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => revoke.mutate(s.id)}>
                      {t('admin.account.revoke')}
                    </Button>
                    <HelpTip k="account.sessions_revoke" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <MobileNotificationsSection />

      {(session.data?.memberships.length ?? 0) > 1 && (
        <Section title={t('admin.account.organizations')}>
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {session.data!.memberships.map((m) => (
              <li key={m.organizationId} className="flex flex-wrap items-center gap-x-4 px-4 py-3">
                <span className="flex-1 font-medium">{m.organizationName}</span>
                <span className="text-sm text-ink-2">{t(`roles.${m.roleKey}`)}</span>
                {m.organizationId === session.data!.session.activeOrganizationId ? (
                  <Badge tone="pine">{t('admin.account.current')}</Badge>
                ) : (
                  <Button size="sm" onClick={() => switchOrg.mutate(m.organizationId)}>
                    {t('admin.account.switchTo')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </PageFrame>
  );
}
